// The Ask User question card from the chat component gallery: single-choice
// rows with a trailing check, multi-choice rows with a leading checkbox, an
// optional free-text "other" row, and a Skip / Continue action pair. Fully
// controlled — selection, text, and submission live with the caller so the
// visual layer remains independent of mutation transport.
import { Check } from 'lucide-react';
import { useReducedMotion } from 'motion/react';
import { type ReactNode, useEffect, useId, useState } from 'react';

import {
    draftIssue,
    draftIssueCopy,
    MESSAGE_CHARACTER_LIMIT,
} from '@apps/web/features/phoebe_chat_v3/model/draft_rules';
import { CompactReceipt } from '@apps/web/features/phoebe_chat_v3/ui/primitives/compact_receipt';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import { cn } from '@apps/web/lib/utils';

export type QuestionChoice = Readonly<{
    /** Stable row identity for selection state — not necessarily the answer value. */
    readonly id: string;
    readonly value: string;
    readonly label: string;
    readonly description?: string;
}>;

export type QuestionAnswerCardProps = Readonly<{
    readonly question: string;
    readonly choices: ReadonlyArray<QuestionChoice>;
    /** Choice ids currently selected — see {@link QuestionChoice.id}. */
    readonly selectedValues: ReadonlySet<string>;
    readonly multiple?: boolean;
    readonly onSelectChoice: (choiceId: string) => void;
    /** Free-text answer row; hidden when null. */
    readonly otherText: string | null;
    readonly onOtherTextChange?: (text: string) => void;
    readonly otherPlaceholder?: string;
    /** Accessible name for the free-text row — placeholders alone do not
        name a textbox for screen readers. */
    readonly otherAriaLabel?: string;
    readonly onSkip: () => void;
    readonly onContinue: () => void;
    readonly disabled?: boolean;
    /** The answer is submitting and waiting for its committed transcript event. */
    readonly submitting?: boolean;
    readonly status?: 'waiting' | 'answered' | 'cancelled' | 'expired';
    /** Coordinator-facing answer reconstructed from committed tool output. */
    readonly resolvedAnswer?: string | null;
}>;

export function QuestionAnswerCard({
    question,
    choices,
    selectedValues,
    multiple = false,
    onSelectChoice,
    otherText,
    onOtherTextChange,
    otherPlaceholder = 'Tell Phoebe what to do…',
    otherAriaLabel = 'Answer in your own words',
    onSkip,
    onContinue,
    disabled = false,
    submitting = false,
    status = 'waiting',
    resolvedAnswer = null,
}: QuestionAnswerCardProps): ReactNode {
    const reduceMotion = useReducedMotion();
    const [showSubmittingReceipt, setShowSubmittingReceipt] = useState(false);
    const otherIssueId = useId();
    const choiceLabelIdPrefix = useId();
    const questionLabelId = useId();
    const otherIssue =
        otherText === null
            ? null
            : draftIssue(otherText, {
                  limit: MESSAGE_CHARACTER_LIMIT,
                  required: false,
              });
    const hasAnswer =
        (selectedValues.size > 0 ||
            (otherText !== null && otherText.trim() !== '')) &&
        otherIssue === null;
    const waiting = status === 'waiting';
    const selectedChoiceLabels = choices
        .filter((choice) => selectedValues.has(choice.id))
        .map((choice) => choice.label);
    const otherAnswer = otherText?.trim() ?? '';
    const submittingAnswer = [
        ...selectedChoiceLabels,
        ...(otherAnswer === '' ? [] : [otherAnswer]),
    ].join(', ');

    useEffect(() => {
        if (!submitting) {
            setShowSubmittingReceipt(false);
            return;
        }
        const handle = window.setTimeout(
            () => setShowSubmittingReceipt(true),
            reduceMotion === true ? 0 : 140,
        );
        return () => window.clearTimeout(handle);
    }, [reduceMotion, submitting]);

    if (waiting && showSubmittingReceipt) {
        return (
            <div data-state="submitting-receipt">
                <CompactReceipt
                    surface="question"
                    title={question}
                    status="running"
                    ariaLabel="Question response sending"
                    statusLabel="Sending"
                    summary={
                        submittingAnswer === '' ? undefined : (
                            <ResolvedReferenceText text={submittingAnswer} />
                        )
                    }
                />
            </div>
        );
    }

    if (!waiting) {
        const answered = status === 'answered';
        const answer = resolvedAnswer || '';
        const choiceIndex = choices.findIndex(
            (choice) => choice.label === answer || choice.value === answer,
        );
        return (
            <div data-state={status}>
                <CompactReceipt
                    surface="question"
                    title={question}
                    status={answered ? 'succeeded' : 'skipped'}
                    ariaLabel={
                        answered ? 'Question answered' : 'Question skipped'
                    }
                    statusLabel={answered ? 'Answered' : 'Skipped'}
                    summary={
                        answered && answer !== '' ? (
                            <ResolvedReferenceText text={answer} />
                        ) : undefined
                    }
                    marker={
                        answered && !multiple && choiceIndex >= 0
                            ? String.fromCharCode(65 + choiceIndex)
                            : undefined
                    }
                />
            </div>
        );
    }

    return (
        <div
            data-state={submitting ? 'submitting' : status}
            className="bg-card @container/question-card min-w-0 overflow-hidden rounded-lg border shadow-[0_2px_6px_rgba(0,0,0,0.1)]"
        >
            <div
                id={questionLabelId}
                className="text-v3-chat px-[15px] pt-[15px] pb-[11px] font-semibold"
            >
                <ResolvedReferenceText text={question} />
            </div>
            <div
                role="group"
                aria-labelledby={questionLabelId}
                className="flex flex-col gap-1.5 px-3"
            >
                {choices.map((choice, index) => {
                    const selected = selectedValues.has(choice.id);
                    const labelId = `${choiceLabelIdPrefix}-${index}`;
                    return (
                        <button
                            key={choice.id}
                            type="button"
                            aria-labelledby={labelId}
                            role={multiple ? 'checkbox' : undefined}
                            aria-checked={multiple ? selected : undefined}
                            aria-pressed={multiple ? undefined : selected}
                            disabled={disabled || !waiting}
                            onClick={() => onSelectChoice(choice.id)}
                            className={cn(
                                'flex min-h-[37px] items-center',
                                multiple ? 'justify-start' : 'justify-between',
                                'gap-2.5 rounded-md border px-2.5 py-2',
                                'text-left text-v3-chat font-semibold',
                                'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                                'transition-[color,background-color,border-color,transform,opacity] duration-150 ease-[cubic-bezier(0.16,1,0.3,1)]',
                                'active:scale-[0.99] motion-reduce:transition-none motion-reduce:active:scale-100',
                                submitting && !selected
                                    ? 'opacity-30'
                                    : 'opacity-100',
                                selected
                                    ? 'border-agent-action-border bg-agent-action-wash text-foreground'
                                    : 'bg-card text-foreground/80 hover:bg-accent',
                            )}
                        >
                            {multiple ? (
                                <span
                                    aria-hidden="true"
                                    className={cn(
                                        'flex size-4 shrink-0 items-center justify-center rounded-[4px] border',
                                        selected
                                            ? 'border-agent-action bg-agent-action text-white'
                                            : 'border-border bg-background',
                                    )}
                                >
                                    {selected ? (
                                        <Check className="size-3" />
                                    ) : null}
                                </span>
                            ) : null}
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5 [overflow-wrap:anywhere]">
                                <span id={labelId}>
                                    <ResolvedReferenceText
                                        text={choice.label}
                                    />
                                </span>
                                {choice.description !== undefined && (
                                    <span className="text-foreground/75 dark:text-muted-foreground text-v3-chat-meta font-normal">
                                        <ResolvedReferenceText
                                            text={choice.description}
                                        />
                                    </span>
                                )}
                            </span>
                            {multiple ? null : (
                                <Check
                                    aria-hidden="true"
                                    className={cn(
                                        'size-[15px] transition-opacity duration-150 motion-reduce:transition-none',
                                        selected
                                            ? 'text-agent-action dark:text-violet-300 opacity-100'
                                            : 'opacity-0',
                                    )}
                                />
                            )}
                        </button>
                    );
                })}
                {otherText !== null && (
                    <label
                        className={cn(
                            'focus-within:border-agent-action flex min-h-[37px] items-center gap-2 rounded-md border px-2.5 py-[7px]',
                            'transition-[background-color,border-color,box-shadow,opacity] duration-150 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none',
                            otherAnswer !== '' &&
                                'border-agent-action-border bg-agent-action-wash',
                            submitting && otherAnswer === ''
                                ? 'opacity-30'
                                : 'opacity-100',
                        )}
                    >
                        <input
                            type="text"
                            aria-label={otherAriaLabel}
                            aria-describedby={
                                otherIssue === null ? undefined : otherIssueId
                            }
                            value={otherText}
                            placeholder={otherPlaceholder}
                            disabled={disabled}
                            readOnly={onOtherTextChange === undefined}
                            onChange={(event) =>
                                onOtherTextChange?.(event.currentTarget.value)
                            }
                            className="text-v3-chat min-w-0 flex-1 border-0 bg-transparent outline-0"
                        />
                    </label>
                )}
                {otherIssue !== null && (
                    <p
                        id={otherIssueId}
                        className="text-muted-foreground text-v3-chat-meta"
                    >
                        {draftIssueCopy(otherIssue, 'Answer')}
                    </p>
                )}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 px-[13px] pt-1 pb-[11px]">
                <button
                    type="button"
                    aria-label="Cancel question"
                    disabled={disabled}
                    onClick={onSkip}
                    className="text-muted-foreground focus-visible:ring-ring/50 text-v3-chat min-h-[33px] rounded-md px-3 py-[7px] font-semibold transition-opacity duration-150 outline-none focus-visible:ring-[3px] motion-reduce:transition-none"
                >
                    Skip
                </button>
                <button
                    type="button"
                    disabled={disabled || !hasAnswer}
                    onClick={onContinue}
                    aria-live="polite"
                    className={cn(
                        'focus-visible:ring-ring/50 min-h-[33px] rounded-md border px-3 py-[7px] text-v3-chat font-semibold text-white outline-none',
                        'transition-[background-color,border-color,transform,opacity] duration-150 ease-[cubic-bezier(0.16,1,0.3,1)]',
                        'active:scale-[0.97] focus-visible:ring-[3px] motion-reduce:transition-none motion-reduce:active:scale-100',
                        submitting
                            ? 'border-agent-positive bg-agent-positive opacity-100'
                            : 'border-agent-action bg-agent-action hover:border-agent-action-strong hover:bg-agent-action-strong disabled:hover:border-agent-action disabled:hover:bg-agent-action disabled:opacity-[0.42]',
                    )}
                >
                    {submitting ? 'Sending…' : 'Continue'}
                </button>
            </div>
        </div>
    );
}
